import React, { useEffect, useMemo, useState } from "react";
import { exportToCsv, commonUserColumns } from "../../utils/exportCsv";
import ActionIconButton from "../../Components/ActionIconButton";
import ActionsCell from "../../Components/ActionsCell";
import {
  AlertTriangle,
  Briefcase,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Edit3,
  Eye,
  Filter,
  Loader2,
  Mail,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Send,
  Shield,
  Trash2,
  User2,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { Modal, message } from "antd";
import { API_BASE_URL } from "../../config";

// Constants
const server_url = API_BASE_URL;

const colors = {
    orange: "#D67C2C",
    blue: "#2173B7",
    green: "#22c55e",
    purple: "#a855f7",
    red: "#ef4444"
};

// Role display names - All use orange theme to match View Teachers/Students/Parents
const ROLE_DISPLAY = {
    tech_leader: { name: "Tech Leader", icon: Shield, color: colors.orange },
    hod: { name: "Head of Department", icon: Briefcase, color: colors.orange },
    finance: { name: "Finance", icon: Briefcase, color: colors.orange },
    cocurricular: { name: "Co-curricular", icon: Users, color: colors.orange },
    management: { name: "Management", icon: UserCheck, color: colors.orange }
};

// Derives the same 3-value status (Pending / Active / Inactive) shown in the
// Status column, so the Status filter matches exactly what the table renders.
function getDerivedStatus(s) {
    if (s.is_temp_password) return "pending";
    if (s.is_active) return "active";
    return "inactive";
}

function ViewTechLeaders() {
    const roleType = "tech_leader";
    const roleInfo = ROLE_DISPLAY[roleType];
    const RoleIcon = roleInfo.icon;

    // State
    const [staff, setStaff] = useState([]);
    const [filteredStaff, setFilteredStaff] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filter state
    const [filters, setFilters] = useState({
        department: "",
        status: ""
    });

    // Add Staff Modal
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [formStaff, setFormStaff] = useState({
        name: "",
        email: "",
        department: "",
        grade_levels: []
    });
    const [submitting, setSubmitting] = useState(false);

    // Detail Modal
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);

    // Edit Modal
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editForm, setEditForm] = useState({});

    // Delete Confirmation
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [staffToDelete, setStaffToDelete] = useState(null);

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const totalPages = Math.ceil(filteredStaff.length / pageSize);
    const pagedStaff = filteredStaff.slice((page - 1) * pageSize, page * pageSize);

    // Notification
    const [notification, setNotification] = useState(null);

    const accessToken = localStorage.getItem("accessToken");

    // Fetch staff on mount
    useEffect(() => {
        fetchStaff();
        // eslint-disable-next-line
    }, []);

    // Distinct department values for the Department filter dropdown — department is
    // a free-text field with no canonical list, so we extract every distinct value
    // currently present across the loaded staff.
    const departmentOptions = useMemo(() => {
        const values = new Set();
        staff.forEach((s) => { if (s.department) values.add(s.department); });
        return Array.from(values).sort();
    }, [staff]);

    // Filter when search or filters change
    useEffect(() => {
        const filtered = staff.filter(s => {
            const matchesSearch = !search ||
                [s.name, s.email, s.department].filter(Boolean).join(" ").toLowerCase().includes(search.toLowerCase());
            const matchesDepartment = !filters.department || s.department === filters.department;
            const matchesStatus = !filters.status || getDerivedStatus(s) === filters.status;
            return matchesSearch && matchesDepartment && matchesStatus;
        });
        setFilteredStaff(filtered);
        setPage(1);
    }, [staff, search, filters]);

    // Clear all filters
    const clearFilters = () => {
        setFilters({ department: "", status: "" });
        setSearch("");
    };

    // Fetch staff from API
    async function fetchStaff() {
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch(`${server_url}/admin/staff/role/${roleType}`, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (!resp.ok) throw new Error("Failed to fetch staff");
            const data = await resp.json();
            setStaff(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message);
            setStaff([]);
        } finally {
            setLoading(false);
        }
    }

    // Show notification
    // Export the whole filtered set, not just the page on screen.
    const handleExportCsv = () => {
        const rows = filteredStaff;
        if (!rows.length) return setNotification({ type: "error", message: "There is nothing to export." });
        const count = exportToCsv(
            roleInfo.name.toLowerCase().replace(/\s+/g, "_"),
            [
                ...commonUserColumns,
                { header: "Department", value: (s) => s.department || s.department_name },
                { header: "Status", value: (s) => (s.is_active === false ? "Inactive" : "Active") },
                { header: "Phone", value: (s) => s.phone_number || s.phone },
            ],
            rows
        );
        setNotification({ type: "success", message: `Exported ${count} records to CSV.` });
    };

    const showNotification = (type, msg) => {
        setNotification({ type, message: msg });
        setTimeout(() => setNotification(null), 5000);
    };

    // Create staff
    async function handleCreateStaff(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const resp = await fetch(`${server_url}/admin/staff`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    ...formStaff,
                    role: roleType
                })
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.detail || "Failed to create staff");
            setAddModalOpen(false);
            setFormStaff({ name: "", email: "", department: "", grade_levels: [] });
            fetchStaff();
            showNotification("success", `${roleInfo.name} created successfully! Welcome email sent.`);
        } catch (err) {
            showNotification("error", err.message);
        } finally {
            setSubmitting(false);
        }
    }

    // Update staff
    async function handleUpdateStaff(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const resp = await fetch(`${server_url}/admin/staff/${selectedStaff.id}`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(editForm)
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.detail || "Failed to update staff");
            setEditModalOpen(false);
            fetchStaff();
            showNotification("success", "Staff updated successfully!");
        } catch (err) {
            showNotification("error", err.message);
        } finally {
            setSubmitting(false);
        }
    }

    // Delete staff
    async function handleDeleteStaff() {
        try {
            const resp = await fetch(`${server_url}/admin/staff/${staffToDelete.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (!resp.ok) {
                const data = await resp.json();
                throw new Error(data.detail || "Failed to delete staff");
            }
            setDeleteModalOpen(false);
            setStaffToDelete(null);
            fetchStaff();
            showNotification("success", "Staff deleted successfully!");
        } catch (err) {
            showNotification("error", err.message);
        }
    }

    // Resend welcome email
    async function handleResendWelcome(staffId) {
        try {
            const resp = await fetch(`${server_url}/admin/staff/resend-welcome/${staffId}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (!resp.ok) throw new Error("Failed to resend email");
            showNotification("success", "Welcome email resent successfully!");
        } catch (err) {
            showNotification("error", err.message);
        }
    }

    // Stats
    const totalCount = staff.length;
    const activeCount = staff.filter(s => s.is_active).length;
    const pendingCount = staff.filter(s => s.is_temp_password).length;

    // StatsCard
    const StatsCard = ({ title, value, icon: Icon, color, bgColor, subtitle }) => (
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
            <div className="p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500">{title}</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
                        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
                    </div>
                    <div className="p-3 rounded-full" style={{ backgroundColor: bgColor }}>
                        <Icon className="w-6 h-6" style={{ color: color }} />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

                    {/* Notification */}
                    {notification && (
                        <div
                            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${notification.type === 'success' ? 'bg-green-500 text-white' :
                                notification.type === 'error' ? 'bg-red-500 text-white' :
                                    'bg-blue-500 text-white'
                                }`}
                        >
                            {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
                                notification.type === 'error' ? <AlertTriangle className="w-5 h-5" /> :
                                    <AlertTriangle className="w-5 h-5" />}
                            <span>{notification.message}</span>
                            <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {/* Header */}
                    <div className="pt-8 pb-8 text-center relative overflow-hidden">
                        <div className="relative z-10">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                                style={{ backgroundColor: roleInfo.color }}>
                                <RoleIcon className="w-8 h-8 text-white" />
                            </div>
                            <h1 className="text-5xl font-bold bg-clip-text text-transparent mb-4"
                                style={{ backgroundColor: roleInfo.color, WebkitBackgroundClip: 'text', color: roleInfo.color }}>
                                {roleInfo.name} Management
                            </h1>
                            <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                                Manage {roleInfo.name.toLowerCase()} users with role-based access control
                            </p>
                        </div>
                    </div>

                    {/* Dashboard Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                        <StatsCard
                            title={`Total ${roleInfo.name}s`}
                            value={totalCount}
                            icon={Users}
                            color={roleInfo.color}
                            bgColor={`${roleInfo.color}15`}
                        />
                        <StatsCard
                            title="Active Users"
                            value={activeCount}
                            icon={UserCheck}
                            color={colors.green}
                            bgColor={`${colors.green}15`}
                        />
                        <StatsCard
                            title="Pending Activation"
                            value={pendingCount}
                            icon={Mail}
                            color={colors.orange}
                            bgColor={`${colors.orange}15`}
                            subtitle="Awaiting password change"
                        />
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                        <div className="p-6 border-b border-gray-100 bg-gray-50">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: roleInfo.color }}>
                                        <Filter className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="text-xl font-semibold text-gray-900">Filter {roleInfo.name}s</h2>
                                </div>

                                <div className="flex flex-wrap items-center gap-3">
                                    <button
                                        onClick={() => setAddModalOpen(true)}
                                        className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                                        style={{ backgroundColor: roleInfo.color }}
                                    >
                                        <Plus className="w-5 h-5" />
                                        Add {roleInfo.name}
                                    </button>

                                    <button
                                        onClick={handleExportCsv}
                                        className="flex items-center gap-2 px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors"
                                    >
                                        <Download className="w-5 h-5" />
                                        Export CSV
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="flex flex-wrap gap-4 items-end">
                                <div className="flex-1 min-w-[250px]">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Search className="inline-block w-4 h-4 mr-2" />
                                        Search
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search by name or email..."
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                                    </div>
                                </div>

                                <div className="min-w-[200px]">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Briefcase className="inline-block w-4 h-4 mr-2" />
                                        Department
                                    </label>
                                    <select
                                        value={filters.department}
                                        onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">All Departments</option>
                                        {departmentOptions.map((dept) => (
                                            <option key={dept} value={dept}>{dept}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="min-w-[200px]">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <CheckCircle className="inline-block w-4 h-4 mr-2" />
                                        Status
                                    </label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="pending">Pending</option>
                                    </select>
                                </div>

                                <button
                                    onClick={fetchStaff}
                                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    Refresh
                                </button>

                                <button
                                    onClick={clearFilters}
                                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Staff Table */}
                    <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
                        <div className="p-6 border-b border-gray-100 bg-gray-50">
                            <h2 className="text-xl font-semibold text-gray-900">{roleInfo.name} List</h2>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                            </div>
                        ) : error ? (
                            <div className="text-center py-16">
                                <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                                <p className="text-gray-600">{error}</p>
                                <button onClick={fetchStaff} className="mt-4 text-blue-600 hover:underline">
                                    Try again
                                </button>
                            </div>
                        ) : pagedStaff.length === 0 ? (
                            <div className="text-center py-16">
                                <User2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500">No {roleInfo.name.toLowerCase()}s found</p>
                                <button
                                    onClick={() => setAddModalOpen(true)}
                                    className="mt-4 text-blue-600 hover:underline"
                                >
                                    Add your first {roleInfo.name.toLowerCase()}
                                </button>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {pagedStaff.map((s) => (
                                            <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                                                            style={{ backgroundColor: roleInfo.color }}>
                                                            {s.name?.charAt(0)?.toUpperCase() || "?"}
                                                        </div>
                                                        <span className="font-medium text-gray-900">{s.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-gray-600">{s.email}</td>
                                                <td className="px-6 py-4 text-gray-600">{s.department || "-"}</td>
                                                <td className="px-6 py-4">
                                                    {s.is_temp_password ? (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                            Pending
                                                        </span>
                                                    ) : s.is_active ? (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                            Inactive
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <ActionsCell className="justify-end">
                                                        <ActionIconButton
                                                            icon={Eye}
                                                            tone="view"
                                                            onClick={() => { setSelectedStaff(s); setDetailModalOpen(true); }}
                                                            title="View Details"
                                                        />
                                                        <ActionIconButton
                                                            icon={Edit3}
                                                            tone="edit"
                                                            onClick={() => { setSelectedStaff(s); setEditForm({ name: s.name, department: s.department, is_active: s.is_active }); setEditModalOpen(true); }}
                                                            title="Edit"
                                                        />
                                                        {s.is_temp_password && (
                                                            <ActionIconButton
                                                                icon={Send}
                                                                tone="approve"
                                                                onClick={() => handleResendWelcome(s.id)}
                                                                title="Resend Welcome Email"
                                                            />
                                                        )}
                                                        <ActionIconButton
                                                            icon={Trash2}
                                                            tone="delete"
                                                            onClick={() => { setStaffToDelete(s); setDeleteModalOpen(true); }}
                                                            title="Delete"
                                                        />
                                                    </ActionsCell>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {filteredStaff.length > 0 && (
                            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <p className="text-sm text-gray-500">
                                        Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, filteredStaff.length)} of {filteredStaff.length}
                                    </p>
                                    <select
                                        value={pageSize}
                                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                                        className="text-sm border border-gray-300 rounded px-2 py-1 text-gray-600"
                                    >
                                        <option value={10}>10 / page</option>
                                        <option value={20}>20 / page</option>
                                        <option value={50}>50 / page</option>
                                        <option value={100}>100 / page</option>
                                    </select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <span className="px-4 py-2 text-sm text-gray-700">
                                        Page {page} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Add Staff Modal */}
                <Modal
                    open={addModalOpen}
                    onCancel={() => setAddModalOpen(false)}
                    footer={null}
                    title={<span className="text-xl font-semibold">Add New {roleInfo.name}</span>}
                    width={500}
                >
                    <form onSubmit={handleCreateStaff} className="space-y-4 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                            <input
                                type="text"
                                required
                                value={formStaff.name}
                                onChange={(e) => setFormStaff({ ...formStaff, name: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter full name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                            <input
                                type="email"
                                required
                                value={formStaff.email}
                                onChange={(e) => setFormStaff({ ...formStaff, email: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter email address"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                            <input
                                type="text"
                                value={formStaff.department}
                                onChange={(e) => setFormStaff({ ...formStaff, department: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., IT, Science, Administration"
                            />
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-700">
                            <p>🔐 A temporary password will be generated and sent to the user's email address.</p>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => setAddModalOpen(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                                style={{ backgroundColor: roleInfo.color }}
                            >
                                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                Create {roleInfo.name}
                            </button>
                        </div>
                    </form>
                </Modal>

                {/* Detail Modal */}
                <Modal
                    open={detailModalOpen}
                    onCancel={() => setDetailModalOpen(false)}
                    footer={null}
                    title={<span className="text-xl font-semibold">Staff Details</span>}
                    width={500}
                >
                    {selectedStaff && (
                        <div className="space-y-4 mt-4">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                                    style={{ backgroundColor: roleInfo.color }}>
                                    {selectedStaff.name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">{selectedStaff.name}</h3>
                                    <p className="text-gray-500">{roleInfo.name}</p>
                                </div>
                            </div>
                            <div className="border-t pt-4 space-y-3">
                                <div className="flex items-center gap-3">
                                    <Mail className="w-5 h-5 text-gray-400" />
                                    <span className="text-gray-700">{selectedStaff.email}</span>
                                </div>
                                {selectedStaff.phone_number && (
                                    <div className="flex items-center gap-3">
                                        <Phone className="w-5 h-5 text-gray-400" />
                                        <span className="text-gray-700">{selectedStaff.phone_number}</span>
                                    </div>
                                )}
                                {selectedStaff.department && (
                                    <div className="flex items-center gap-3">
                                        <Briefcase className="w-5 h-5 text-gray-400" />
                                        <span className="text-gray-700">{selectedStaff.department}</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2 pt-4">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedStaff.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                    {selectedStaff.is_active ? 'Active' : 'Inactive'}
                                </span>
                                {selectedStaff.is_temp_password && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                                        Pending Activation
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </Modal>

                {/* Edit Modal */}
                <Modal
                    open={editModalOpen}
                    onCancel={() => setEditModalOpen(false)}
                    footer={null}
                    title={<span className="text-xl font-semibold">Edit Staff</span>}
                    width={500}
                >
                    <form onSubmit={handleUpdateStaff} className="space-y-4 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                            <input
                                type="text"
                                required
                                value={editForm.name || ""}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                            <input
                                type="text"
                                value={editForm.department || ""}
                                onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={editForm.is_active || false}
                                onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                                className="w-4 h-4 text-blue-600 rounded"
                            />
                            <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => setEditModalOpen(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                Save Changes
                            </button>
                        </div>
                    </form>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    open={deleteModalOpen}
                    onCancel={() => setDeleteModalOpen(false)}
                    footer={null}
                    title={<span className="text-xl font-semibold text-red-600">Delete Staff</span>}
                    width={400}
                >
                    <div className="mt-4">
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to delete <strong>{staffToDelete?.name}</strong>? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteModalOpen(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteStaff}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </>
    );
}

export default ViewTechLeaders;
